// Shared presentation for the dedicated /pricing route and its pricing subcomponents. Fixed
// self-serve values come only from the
// reconciled billing catalogue; the separate per-student configuration is never substituted for a
// missing fixed price. Both surfaces therefore disclose the same verified/unavailable state.
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Check, Network, Sparkles, Sprout, Star, TrendingDown, Wallet, Zap } from "lucide-react";
import { PlanModulesList, PlanModulesToggle } from "./PlanModules";
import { cn, inrFull, inrExactPaise } from "../../lib/utils";
// Campaign helpers live in ./campaignStrip (kept OUT of the eager Landing chunk — see its header);
// re-exported here so /pricing-side consumers keep one import surface.
import { campaignFrom, campaignDiscountPaise, campaignTheme, inrCampaignExact } from "./campaignStrip";
export { campaignFrom, campaignDiscountPaise, campaignTheme, campaignEndLabel, CampaignStrip, inrCampaignExact } from "./campaignStrip";
import { useMarketingMotion } from "../../components/marketing/MarketingMotion";
import { StaggerItem, PRICING_ICONS, PLAN_ICONS, PRICE_BTN_BASE, PRICE_BTN } from "./parts1";
import {
  annualPaise, enterpriseIndicative, monthlyEquivalentPaise, termPricePaise, toStudents,
} from "../../lib/pricingTiers";
import { CTA_PRIMARY_LABEL } from "../../components/marketing/navigation";
import { fillCopyTokens } from "../../lib/marketingCounts";

export function pricingTaxDisplay(pricingIntegrity) {
  const value = pricingIntegrity?.tax_display;
  return typeof value === "string" && value.trim()
    ? value.trim()
    : "Tax treatment unavailable";
}

// Fixed-plan cards from the served pricing config — same shape as the CMS `pricing` cards.
// Static copy mirrors the tier narrative; the annual note rides `annual`, not the feature list.
// `pricingPage` (cms.pricing_page) may carry platform-published `plan_cards` — WORDS only
// (audience line, bullet points, CTA label) plus card ORDER/presence; price, period, the billed/
// saving lines and the verified state ALWAYS derive from the reconciled billing catalogue, and a
// CMS entry whose id has no built-in card renders nothing (never an unpriced invented card).
// The built-in copy below byte-mirrors core/marketing.py DEFAULTS["pricing_page"].plan_cards
// (test_marketing_copy_mirror_contract.py) — change BOTH together.
// The prepay ladder as served in `pricing_model.subscription_terms`. Monthly-only is the safe
// fallback: a page that cannot read the ladder must offer the un-discounted baseline rather than
// invent a discount.
const MONTHLY_TERM = Object.freeze({ key: "monthly", label: "Monthly", months: 1, price_bps: 10000, discount_bps: 0 });

export function subscriptionTerms(priceCfg) {
  const terms = priceCfg?.subscription_terms;
  return Array.isArray(terms) && terms.length ? terms : [MONTHLY_TERM];
}

export function findTerm(priceCfg, key) {
  const terms = subscriptionTerms(priceCfg);
  return terms.find((t) => t.key === key) || terms[0];
}


// Plan-card WORDS when the payload carries none. Byte-mirrors core/marketing.py
// DEFAULTS["pricing_page"].plan_cards (test_marketing_copy_mirror_contract.py) — change BOTH
// together. Copy is CMS-owned in normal operation; this exists so a failed fetch still renders a
// card a reader can understand, rather than a name and a button.
//
// Only WORDS. No price, rate or minimum appears here: a fallback figure is a price nobody
// approved being published under our name, and the card says "we couldn't load prices" instead.
const PLAN_CARD_FALLBACK = Object.freeze([
  Object.freeze({
    id: "plan_free",
    audience: "For a school of up to 100 students",
    cta: "Start free",
    points: "All {core_modules} core modules — nothing held back\nFees, invoices and numbered receipts\nAttendance, timetable, exams and report cards\nCertificates, transfer and bonafide letters\nParent and student apps, notices and class chat\nFree forever under 100 students — not a trial",
  }),
  Object.freeze({
    id: "plan_starter",
    audience: "For a school moving its back office off paper",
    cta: "Choose Essentials",
    points: "Everything in Free, plus report registers in PDF and Excel\nMigration Copilot imports your old spreadsheets\nStaff leave, from apply to approve\nLibrary issue, return and fines\nFront office: visitors, calls and gate passes",
  }),
  Object.freeze({
    id: "plan_growth",
    audience: "For a school running the whole campus, with Aira",
    cta: "Choose Growth",
    points: "Everything in Essentials, plus Aira AI — it asks before every change\nVoice dictation in English and 10 Indian languages\nFee recovery that stops the moment a parent pays\nAccounting, Tally export and GL statements\nHostel, inventory, health room and scholarships\n40 starter automation rules and at-risk student lists\n5,000 AI credits a month",
  }),
  Object.freeze({
    id: "plan_premium",
    audience: "For a school that wants the AI and payroll layer",
    cta: "Try Pro free for 90 days",
    points: "Everything in Growth, plus AI Meeting Notes\nConsent first, and the audio is discarded — never stored\nHR and Payroll, with 33 governed HR AI capabilities\nAutomation builder: draw a rule, simulate it, ship it\nWorkforce analytics and a public REST API\n20,000 AI credits a month, or bring your own model",
  }),
  Object.freeze({
    id: "plan_enterprise",
    audience: "For a trust or chain running more than one school",
    cta: "Talk to us",
    points: "Everything in Pro, plus your brand on the app, portal and PDFs\nBiometric, QR and RFID attendance devices\nVendor payouts with TDS and UTR tracking\nMCP access — connect your own AI tools\nUnmetered AI credits and volume pricing\nMulti-campus group console (rolling out)",
  }),
]);

// The five public plan cards, priced for ONE school's roster.
//
// Identity, copy and order come from the CMS (`pricing_page.plan_cards`); every FIGURE comes from
// the governed price book through `lib/pricingTiers`. A card with no verified price renders its
// rate and an honest "we couldn't load prices" state rather than a number — publishing a fallback
// price under our own name is the failure this whole redesign exists to remove.
export function buildTierCards(model, pricingIntegrity, pricingPage, termKey, students, counts) {
  const taxDisplay = pricingTaxDisplay(pricingIntegrity);
  const term = findTerm({ subscription_terms: model?.terms }, termKey);
  const verified = Boolean(model?.verified) && pricingIntegrity?.status !== "unavailable";
  const cmsCards = Array.isArray(pricingPage?.plan_cards) && pricingPage.plan_cards.length
    ? pricingPage.plan_cards
    : PLAN_CARD_FALLBACK;
  const copyById = Object.fromEntries(
    cmsCards.filter((c) => c && typeof c.id === "string").map((c) => [c.id, c]),
  );
  const n = toStudents(students);

  const monthlyEligible = model?.monthlyEligiblePlans || [];
  const tierCard = (planId, name) => {
    const tier = (model?.tiers || []).find((t) => t.planId === planId) || null;
    const copy = copyById[planId] || {};
    // A plan that does not sell this term shows the REASON, not a price. The rule is the RBI
    // AFA per-transaction ceiling: a monthly charge against a large annual minimum is a mandate
    // that would succeed for a small school and fail at checkout for a big one. A zero-rate plan
    // registers no mandate, so it is never restricted.
    const termUnavailable = Boolean(
      verified && term?.key === "monthly" && tier && tier.ratePaise > 0
      && !monthlyEligible.includes(planId),
    );
    const annual = verified && tier && !termUnavailable ? annualPaise(tier, n) : null;
    const pricePaise = annual === null || !n ? null : termPricePaise(annual, term);
    // THE SAVING IS AGAINST THE ALTERNATIVE THIS SCHOOL ACTUALLY HAS.
    //
    // For a plan that sells monthly, that is twelve monthly charges. For a plan that does NOT
    // (Pro, Enterprise — the RBI per-transaction ceiling makes a monthly mandate unsellable
    // there), the alternative to a two-year prepay is paying yearly twice, so that is what it is
    // compared with. Comparing every plan against monthly would have shown Pro no saving at all
    // on a term that genuinely discounts it — an offer the page was hiding from the buyer.
    const terms = model?.terms || [];
    const monthlyTerm = terms.find((t) => t.key === "monthly");
    const yearlyTerm = terms.find((t) => t.key === "yearly");
    const sellsMonthly = Boolean(tier) && (tier.ratePaise === 0 || monthlyEligible.includes(planId));
    let savingVsMonthly = null;
    if (annual && pricePaise && tier?.ratePaise && term.key !== "monthly") {
      const baseline = sellsMonthly && monthlyTerm
        ? termPricePaise(annual, monthlyTerm) * (term.months || 12)
        : (yearlyTerm && (term.months || 12) > 12
            ? termPricePaise(annual, yearlyTerm) * ((term.months || 12) / 12)
            : null);
      if (baseline) savingVsMonthly = Math.max(0, Math.round(baseline) - pricePaise) || null;
    }
    // THE PER-STUDENT FIGURE REFLECTS THE SELECTED TERM, AND DOES NOT NEED A ROSTER TO EXIST.
    //
    // Reported live: switching the term control between Monthly and Yearly left this number
    // completely unchanged, because it was computed from `annual` — the flat, PRE-TERM base —
    // rather than from `pricePaise`, the amount this school actually pays under the term it
    // picked. Monthly carries a surcharge and a multi-year prepay carries a discount (that is
    // what the "You save…" chip above is measuring), so a per-student figure that ignores the
    // term silently drops that surcharge/discount and reports the same number for every choice —
    // which reads as "the calculator is not responding to yearly/monthly at all".
    //
    // WITH a roster: `pricePaise` already carries any plan-minimum floor AND the term's own bps,
    // so dividing it by students is what one student actually costs for the WHOLE selected term;
    // normalizing by `term.months` to a year/month is what makes this figure and the saving chip
    // agree. WITHOUT a roster there is no `pricePaise` yet (it needs a headcount), but a term's
    // multiplier is a plain percentage of the rate that does not need one to apply — so the
    // sticker rate is run through the SAME `termPricePaise` arithmetic the charge path uses,
    // treating the rate as a one-student annual amount, keeping the figure honest to the
    // selected term even before a count is typed. `0` is a real answer for the free plan and for
    // a genuinely zero charge, so every branch here is null-checked and never truthiness-checked.
    //
    // A term this plan does not sell (`termUnavailable` — Pro/Enterprise on Monthly) has no
    // `pricePaise` and no valid multiplier to apply, so it falls back to the flat annual rate,
    // exactly like the big price above it does in that same state.
    let perStudentYear = null;
    if (verified && tier && Number.isFinite(tier.ratePaise)) {
      if (pricePaise !== null && n) {
        const perStudentTermTotal = Math.round(pricePaise / n);
        perStudentYear = Math.round((perStudentTermTotal * 12) / (term.months || 12));
      } else if (!termUnavailable) {
        const stickerTermTotal = termPricePaise(tier.ratePaise, term);
        perStudentYear = Math.round((stickerTermTotal * 12) / (term.months || 12));
      } else {
        perStudentYear = tier.ratePaise;
      }
    }
    const floorApplied = Boolean(
      verified && tier && n && n * tier.ratePaise < tier.floorPaise && tier.floorPaise > 0,
    );
    return {
      planId,
      id: planId,
      name,
      commercialVerified: verified,
      ratePaise: tier ? tier.ratePaise : null,
      floorPaise: tier ? tier.floorPaise : null,
      pricePaise,
      termUnavailable,
      termUnavailableNote: termUnavailable
        ? `${name} is billed annually. Monthly billing is available on Essentials and Growth.`
        : "",
      // THE PER-STUDENT FIGURES. A head reading "₹96,850" is reading a budget line; the same
      // school at "₹12 per student a month" is reading a decision they can make. Both are the
      // SAME price — the small one is simply the one a person can hold in their head, so the card
      // states it explicitly rather than leaving the division to the reader.
      //
      // Derived from the ACTUAL annual charge, not from the rate: a school under the minimum is
      // paying more per student than the sticker rate, and showing the rate there would be the
      // one place this page told a school something its invoice would contradict.
      // THE PER-STUDENT TAG RENDERS ON A COLD LOAD TOO.
      //
      // These were `annual && n ? ... : null`, so with no roster typed they were BOTH null and
      // four of the five cards showed no per-student figure at all — the one number the page is
      // built to lead with was invisible until a visitor interacted. It does not need the roster:
      // with no count the per-student price IS the tier's own published rate, and only once a
      // count is entered does it become something the rate cannot tell you (a school under the
      // minimum pays MORE per head than the sticker rate, which is why the count-derived figure
      // takes over the moment there is one).
      perStudentYearPaise: perStudentYear,
      perStudentMonthPaise: perStudentYear === null ? null : Math.round(perStudentYear / 12),
      // What the chosen term saves against paying MONTHLY for the same span — the only honest
      // comparison, because that is the alternative a school actually has. Null when the term IS
      // monthly (nothing to compare) or when the plan does not sell monthly at all.
      savingVsMonthlyPaise: savingVsMonthly,
      // Named, because "you save ₹19,370" against an unstated baseline is a number a buyer
      // cannot check — and an unverifiable saving reads as marketing rather than as a price.
      savingBasis: savingVsMonthly
        ? (sellsMonthly && monthlyTerm ? "vs paying monthly" : "vs paying yearly")
        : "",
      // The annual figure stated per month, so a school can compare it with what it pays now.
      // NOT what paying monthly costs — that carries the term surcharge.
      monthlyEquivalentPaise: annual ? monthlyEquivalentPaise(annual) : null,
      floorApplied,
      floorNote: floorApplied && tier && tier.ratePaise
        ? `This is the plan minimum — it covers schools up to ${Math.floor(tier.floorPaise / tier.ratePaise).toLocaleString("en-IN")} students.`
        : "",
      // "34 of 58 modules" — the size of what this plan turns on, derived from the live
      // entitlement registries (lib/marketingCounts), never typed. This is the number that
      // reconciles the card with the comparison table below it AND with the 115-capability
      // catalogue on /modules: 58 modules is what a PLAN switches on, 115 capabilities is what
      // a school DOES with them. A page that states one and not the other is why a buyer
      // reading "115" on one page and "23" on the next thinks the prices hide the product.
      modulesIncluded: counts?.byPlan?.[planId] || null,
      modulesTotal: counts?.total || null,
      termLabel: term.label,
      termMonths: term.months,
      taxNote: taxDisplay,
      audience: copy.audience || "",
      // Bullets carry the same copy tokens the rest of the page does, so "All {core_modules} core
      // modules" tracks the live registry instead of freezing a number into published copy.
      points: String(copy.points || "").split("\n")
        .map((x) => fillCopyTokens(x, { counts })).filter(Boolean),
      cta: copy.cta || CTA_PRIMARY_LABEL,
      quoteOnly: false,
      popular: planId === "plan_growth",
    };
  };

  const ent = enterpriseIndicative(n, model);
  const entCopy = copyById.plan_enterprise || {};
  return [
    tierCard("plan_free", "Free"),
    tierCard("plan_starter", "Essentials"),
    tierCard("plan_growth", "Growth"),
    tierCard("plan_premium", "Pro"),
    {
      planId: "plan_enterprise",
      id: "plan_enterprise",
      name: "Enterprise",
      commercialVerified: verified,
      quoteOnly: true,
      ratePaise: verified ? model.enterprise.ratePaise : null,
      minimumPaise: verified ? model.enterprise.minimumPaise : null,
      indicativePaise: verified && n >= (model.enterprise.selfServeMaxStudents || 0) && n > 0
        ? ent.annualPaise : null,
      // "From", never a flat figure: Enterprise also carries implementation, SLA and scope that
      // a calculator cannot see, and stating one number would be a quote we have not given.
      priceLabel: verified ? "from" : "",
      modulesIncluded: counts?.byPlan?.plan_enterprise || null,
      modulesTotal: counts?.total || null,
      audience: entCopy.audience || "",
      points: String(entCopy.points || "").split("\n").map((x) => x.trim()).filter(Boolean),
      cta: entCopy.cta || "Talk to us",
      taxNote: taxDisplay,
      popular: false,
    },
  ];
}


// Join each card's priced figures onto its matrix column, by PLAN ID.
//
// The `plan_trial` branch is gone with the trial column: the trial IS Pro for 90 days, and giving
// it a column made the free trial read as beating every paid plan. One sentence under the table
// says it instead.
export function mapMatrixPlans(plans, tierCards) {
  const byId = Object.fromEntries((tierCards || []).map((card) => [card.planId, card]));
  return (plans || []).map((pl) => {
    const card = byId[pl.id];
    return card
      ? { ...pl, pricePaise: card.pricePaise, ratePaise: card.ratePaise,
          quoteOnly: card.quoteOnly, commercialVerified: card.commercialVerified }
      : pl;
  });
}

// ---------------------------------------------------------------------------------------------
// THE LADDER IS THE PALETTE.
//
// Five cards side by side have to answer "which one is mine?" before they answer "how much?", and
// five identical white cards with one blue border answer neither. So colour here CARRIES MEANING
// rather than decorating: the ramp ASCENDS with the tier and each fill says what the tier IS.
//
//   Free        green         start here, at no cost — the universal "go"
//   Essentials  LOGO ORANGE   the brand's own second colour, on the first paid step
//   Growth      royal blue    the anchor, wearing the brand itself
//   Pro         AI purple     because Pro is where the Aira suite begins. The colour IS the reason,
//                             which is why this is the one marketing surface that uses the AI
//                             token: everywhere else purple would be decoration, here it is the
//                             product.
//   Enterprise  royal ink     contracted, governed, quiet authority
//
// ESSENTIALS WEARS THE ACTUAL LOGO ORANGE (#F97316), and the ink is what makes that possible.
// This card used to be `orange.dark` (#C2410C) — a compromise, taken because white on the logo
// orange measures 2.80:1 and fails AA outright at any size. The orange was never the problem; the
// INK was. Navy (`royal.deep`) on the same logo orange measures 7.48:1 at the top of the gradient
// and 6.04:1 at its deepest point, both comfortably AA for body text — so the card can carry the
// exact brand colour AND be the most readable card in the row. It is now also the only LIGHT fill
// among five, which lands the eye on the first PAID step: a better outcome than the muted orange
// it replaces, arrived at by changing the ink rather than the brand.
//
// Every value is a house token or a measured literal, and every ink pair was MEASURED, never
// assumed: white on green 6.71:1 · NAVY on logo orange 6.04-7.48:1 · white on royal blue 8.88:1 ·
// white on AI purple 8.98:1 · white on royal ink 18.56:1 (each taken at the gradient's DEEPEST
// point, which is the worst case, not at the flat mid-tone).
//
// A fill carrying white ink can never use a theme-aware token — `royal.DEFAULT` inverts to
// near-white in dark and would put white on white — which is why `royal.deep`, `ai.dark` and the
// literals below exist. The same rule binds the price tag: its fill and its ink are BOTH literals.

// THE INKS A FILL CAN CARRY — and why there is no alpha in any of them.
//
// "Bright and high definition" is one instruction, not two. The brightness is the fill; the
// DEFINITION is that no text on this grid is faded. Every ink below is a SOLID measured colour,
// and hierarchy is carried by weight and size instead of by opacity. The previous set built its
// hierarchy out of `text-white/70`-style washes, which is exactly what makes a card read soft —
// and which, on a brighter fill, is also what breaks it: white at 70% over a vivid blue measures
// 2.92:1, so the "quiet" line was the illegible one.
//
// Which ink a card carries follows its fill, because it has to. A saturated blue or purple sits
// in a luminance band where NEITHER white nor navy clears 4.5:1 — that is a fact about the colour,
// not a preference — so the two light fills take navy and the three saturated ones take white.
// Worst case per card, measured at BOTH ends of its gradient: Free 7.42 · Essentials 6.04 ·
// Growth 5.17 · Pro 5.70 · Enterprise 6.29.
const INK_LIGHT = Object.freeze({          // white ink, for the three saturated fills
  ink: "text-white", sub: "text-white", faint: "text-white",
  chip: "bg-white/15 text-white ring-1 ring-white/30",
  rule: "border-white/25",
  tick: "bg-white text-royal-deep",
  saving: "bg-white/18 text-white ring-1 ring-white/35",
  meta: "bg-white/15 text-white ring-1 ring-white/30",
});
const INK_DARK = Object.freeze({           // navy ink, for the two bright fills
  ink: "text-royal-deep", sub: "text-royal-deep", faint: "text-royal-deep",
  chip: "bg-royal-deep/15 text-royal-deep ring-1 ring-royal-deep/30",
  rule: "border-royal-deep/25",
  saving: "bg-royal-deep text-white ring-1 ring-royal-deep",
  meta: "bg-royal-deep/12 text-royal-deep ring-1 ring-royal-deep/30",
});

// THE LADDER IS THE PALETTE, and it now ascends in BRIGHTNESS as well as in hue.
//
//   Free        vivid green    #22C55E   2.6x brighter than the green it replaces
//   Essentials  LOGO ORANGE    #F97316   2.1x brighter — the brand's own second colour
//   Growth      brand blue     #2563EB   the vivid step of the house royal blue
//   Pro         AI purple      #7C3AED   the house `ai` token itself — Pro is where Aira begins,
//                                        so here the AI colour is the product, not decoration
//   Enterprise  vivid indigo   #4338CA   6.9x brighter than the near-black navy it replaces
//
// Growth and Pro are the honest ceiling of this instruction: their bases are ALREADY the vivid
// brand and AI values, and a brighter blue or purple drops white ink under AA (measured: white on
// #3B82F6 is 3.68:1, on #A855F7 is 3.96:1) while navy does not clear it either. They get a
// brighter gradient TOP instead, so the card reads vivid without an illegible ink.
//
// Gradient direction is not decoration either. A navy-ink card runs LIGHTER at the top, because
// navy reads better on lighter; a white-ink card runs the vivid colour at the top and a touch
// deeper at the base, because white reads better on deeper. Every figure above is the worst of
// the two ends, never the midpoint.
const PLAN_SKIN = {
  plan_free: {
    Icon: Sprout,
    on: INK_DARK,
    shell: "bg-[linear-gradient(160deg,#4ADE80_0%,#22C55E_100%)] border border-[#16A34A] text-royal-deep",
    sub: "text-[#0F2A1C]", faint: "text-[#0F2A1C]",      // 6.74:1 at the deepest end
    tick: "bg-royal-deep text-[#4ADE80]",                 // 9.71:1 — a solid navy dot, bright check
    // Measured live: `text-success-dark` (tailwind's static #0F5F2D, an AA-on-WHITE ink) is a
    // SERIOUS color-contrast failure on `/pricing@dark` wherever it lands — on the comparison
    // table's Free-column "Included" pill (`PricingFeatureCatalogue.js` `Availability`, on its own
    // `bg-success/16` fill) AND on that same table's "Free" column header (`font-heading font-bold`,
    // on the header row's `bg-muted`) — because both dark theme's own surfaces AND the static ink
    // are dark, so raising the pill's fill alpha alone (the first attempt: `/10` -> `/16`) could
    // never fix the header, which carries no fill at all. `text-success-text` is the actual fix: a
    // NEW theme-aware ink (mirrors this file's `text-orange-dark` siblings' theme-aware counterparts)
    // that resolves to this same literal in light (byte-identical there) and to a bright teal-green
    // in dark (same value as this theme's own `success` fill — already proven >=4.5:1 against every
    // ground this accent composites onto). `/16` stays (harmless in light, no reason to re-litigate
    // now the ink itself is fixed) rather than reverting to the sibling tiers' `/10`.
    accent: "text-success-text", accentBar: "bg-success", accentSoft: "bg-success/16",
    btn: "bg-royal-deep text-white hover:bg-royal-deep/90 font-bold",
    tag: "bg-royal-deep ring-1 ring-royal-deep [--vp-tag-ring:rgba(13,27,61,0.5)]",
    tagInk: "text-[#4ADE80]", tagSub: "text-[#E2E8F0]",
  },
  plan_starter: {
    Icon: Wallet,
    on: INK_DARK,
    shell: "bg-[linear-gradient(160deg,#FB923C_0%,#F97316_100%)] border border-[#EA580C] text-royal-deep",
    sub: "text-[#431407]", faint: "text-[#431407]",      // 5.58:1 at the deepest end
    tick: "bg-royal-deep text-[#FB923C]",                 // 7.48:1
    accent: "text-orange-dark", accentBar: "bg-orange", accentSoft: "bg-orange/10",
    btn: "bg-royal-deep text-white hover:bg-royal-deep/90 font-bold",
    tag: "bg-royal-deep ring-1 ring-royal-deep [--vp-tag-ring:rgba(13,27,61,0.5)]",
    tagInk: "text-[#FB923C]", tagSub: "text-[#E2E8F0]",
  },
  plan_growth: {
    Icon: Building2,
    on: INK_LIGHT,
    shell: "bg-[linear-gradient(160deg,#2563EB_0%,#1D4ED8_100%)] border border-[#3B82F6] text-white",
    accent: "text-accent-active", accentBar: "bg-primary", accentSoft: "bg-primary/10",
    // Arbitrary-value white on BOTH `btn` and `tag`, never the `bg-white` utility: index.css remaps
    // `.bg-white` to the dark surface token for legacy pre-v2 cards in dark mode, which took this
    // card's dark-slate `tagSub`/blue `tagInk`+`btn` text (all chosen for a literal white ground)
    // down to serious color-contrast failures once the "white" turned navy — `btn` measured this
    // exact failure live (`.text-[#1D4ED8].hover:bg-white/90` on `/pricing@dark`) after `tag` had
    // already been fixed the same way, so fix them together and keep them together. This card's
    // white pill/sticker are deliberate always-white surfaces against the card's own colour, not a
    // legacy card — both must opt out of that remap in both themes.
    btn: "bg-[#ffffff] text-[#1D4ED8] hover:bg-[#ffffff]/90 font-bold",
    tag: "bg-[#ffffff] ring-1 ring-[#ffffff] [--vp-tag-ring:rgba(255,255,255,0.8)]",
    tagInk: "text-[#1D4ED8]", tagSub: "text-[#334155]",
  },
  plan_premium: {
    Icon: Sparkles,
    on: INK_LIGHT,
    shell: "bg-[linear-gradient(160deg,#7C3AED_0%,#6D28D9_100%)] border border-[#8B5CF6] text-white",
    sub: "text-[#EDE9FE]", faint: "text-[#EDE9FE]",      // 4.80:1 at the deepest end
    accent: "text-ai-ink", accentBar: "bg-ai-dark", accentSoft: "bg-ai/10",
    btn: "bg-[#ffffff] text-[#6D28D9] hover:bg-[#ffffff]/90 font-bold",
    tag: "bg-[#ffffff] ring-1 ring-[#ffffff] [--vp-tag-ring:rgba(255,255,255,0.8)]",
    tagInk: "text-[#6D28D9]", tagSub: "text-[#334155]",
  },
  plan_enterprise: {
    Icon: Network,
    on: INK_LIGHT,
    // DEEP TEAL, not the indigo this first became. Indigo measured beautifully (white 6.29:1) and
    // was still wrong: a ladder is only "high definition" if a reader can tell its steps APART,
    // and indigo sat 11.1 from Pro's purple and 10.4 from Growth's blue in OKLab — under the ~15
    // where two fills stop reading as the same colour. Three adjacent blue-violet cards is a
    // gradient, not a ladder. Teal clears both (24.8 from Pro, 18.9 from Growth), keeps white ink
    // at 5.36:1, and is still far brighter than the near-black navy it replaced.
    //
    // Growth -> Pro stays the one narrow step at 12.4, and that is deliberate: Growth wears the
    // brand royal blue and Pro wears the house `ai` purple because Pro is where the Aira suite
    // begins. Both are semantically fixed, so the separation is carried by their borders, glyphs
    // and names rather than by moving a colour that means something.
    shell: "bg-[linear-gradient(160deg,#0E7490_0%,#155E75_100%)] border border-[#0891B2] text-white",
    sub: "text-[#CFFAFE]", faint: "text-[#CFFAFE]",      // 4.79:1 at the deepest end
    accent: "text-accent-active", accentBar: "bg-primary", accentSoft: "bg-primary/10",
    btn: "bg-[#ffffff] text-[#155E75] hover:bg-[#ffffff]/90 font-bold",
    tag: "bg-[#ffffff] ring-1 ring-[#ffffff] [--vp-tag-ring:rgba(255,255,255,0.8)]",
    tagInk: "text-[#155E75]", tagSub: "text-[#334155]",
  },
};

// The plan colours, for surfaces OUTSIDE the card — the comparison table reads this so a column
// there is recognisably the same plan as the card above it. Exported because the table lives in
// its own file and two hand-kept colour maps is one map too many.
export const PLAN_ACCENT = Object.fromEntries(
  Object.entries(PLAN_SKIN).map(([id, v]) => [id, {
    text: v.accent, bar: v.accentBar, soft: v.accentSoft,
  }]),
);

const FALLBACK_SKIN = PLAN_SKIN.plan_growth;

// "/ 1 year" is not how anyone says it. The suffix is the phrase a head would read aloud, and a
// FREE plan has no billing period at all — "₹0 / 1 year" invites the question "and then what?",
// which is the opposite of what a free tier is for.
function termSuffix(pl) {
  if (!pl.ratePaise) return "free forever";
  if (pl.termMonths === 1) return "a month";
  if (pl.termMonths === 12) return "a year";
  return `for ${String(pl.termLabel || "").toLowerCase()}`;
}

// The "about X a month" line is explicitly an APPROXIMATION, so it is shown in whole rupees.
// `inrFull` renders 536250 paise as "₹5,362.5" — a fractional rupee beside a phrase that already
// says "about", which reads as a rounding bug rather than as a comparison aid.
function aboutPerMonth(paise) {
  return inrFull(Math.round((Number(paise) || 0) / 100) * 100);
}

// THE PER-STUDENT PRICE TAG — one component, rendered by every branch of the card that has a
// per-student figure to show.
//
// It was inline markup, and it had already been copied once (the free plan grew its own duplicate)
// while two other branches — the cold load with no roster typed, and a plan that does not sell the
// selected term — simply had none. So the number this page is built to lead with was missing from
// four of the five cards until a visitor interacted with the calculator. One component means a
// branch cannot quietly go without it, and the treatment cannot drift between copies.
//
// `key` on the CALLER is load-bearing: it re-mounts this node when the figure changes, which
// replays the CSS entrance in index.css. That makes the motion responsive — it fires at the moment
// the number becomes true for this school — rather than ambient, which the card may not have.
function PriceTag({ skin, planId, monthPaise, yearPaise, note }) {
  return (
    <div className={cn("vp-price-tag relative mt-4 rounded-2xl px-3.5 py-3",
                       "shadow-[0_12px_26px_-14px_rgba(2,6,23,0.6)]", skin.tag)}
         data-testid={`plan-per-student-${planId}`}>
      <p className={cn("text-[10px] font-bold uppercase tracking-[0.15em]", skin.tagSub)}>
        Per student
      </p>
      <div className="mt-1 flex items-baseline gap-1.5 whitespace-nowrap">
        <span className={cn("font-heading font-extrabold text-[27px] leading-none tracking-tight tabular-nums", skin.tagInk)}>
          {inrFull(monthPaise)}
        </span>
        <span className={cn("text-[13px] font-bold", skin.tagSub)}>a month</span>
      </div>
      <p className={cn("mt-1 text-[11.5px] font-medium tabular-nums", skin.tagSub)}>
        {inrFull(yearPaise)} a year{note}
      </p>
    </div>
  );
}

// One premium plan card, priced for the school's own roster.
// `pl` is a buildTierCards row: {planId,name,pricePaise,ratePaise,points[],audience,cta,...}.
export function PlanCard({ pl, navigate, reduceMotion, onContactSales, modules, modulesExpanded, onToggleModules, campaign = null }) {
  const { paused } = useMarketingMotion();
  const motionOff = reduceMotion || paused;
  const pop = pl.popular;
  // Keyed on the plan ID, never the display name: a name is CMS copy and has already changed once
  // (Starter -> Essentials, Premium -> Pro), which silently emptied a name-keyed lookup.
  // Ink set first, card identity over it: the card names WHICH inks it carries (`on`), because
  // one of the five is a light fill and four are deep, and a shared white-ink map would have put
  // white on the logo orange at 2.80:1.
  const base = PLAN_SKIN[pl.planId] || FALLBACK_SKIN;
  const skin = { ...(base.on || INK_LIGHT), ...base };
  const Icon = skin.Icon;
  const isEnterprise = pl.planId === "plan_enterprise";
  // The governed pricing CAMPAIGN's per-card claim (2026-08-28 spec: "a slim dismissible strip
  // beside the plan grid plus an honest was/now line on covered cards"). Never shown on the
  // contact-sales tier — its checkout takes no coupon and grants no self-serve trial. Two mutually
  // exclusive campaign kinds (core/pricing_config.get_campaign): a trial-length extension states
  // itself in months (no arithmetic, so it needs no verified price); a coupon discount is the
  // served PERCENT applied to this card's OWN already-resolved headline price — never a separate
  // "was" field, so the card can never show a saving the checkout would then compute differently.
  const campaignTrialLine = !isEnterprise && campaign?.trial_months > 0
    ? `${campaign.trial_months}-month free trial for schools joining during the offer — no card needed.`
    : null;
  const campaignDiscount = !isEnterprise && !campaignTrialLine && campaign?.coupon
    && pl.commercialVerified && Number.isInteger(pl.pricePaise) && pl.pricePaise > 0
    ? campaignDiscountPaise(campaign, pl.planId, pl.pricePaise) : 0;
  const campaignLineTheme = campaign ? campaignTheme(campaign) : null;
  return (
    <StaggerItem as="div" v="up"
      whileHover={motionOff ? undefined : { y: -6 }}
      className={cn(
        "group/card relative flex flex-col rounded-[26px] p-6 lg:p-7",
        "transition-[box-shadow,border-color,transform] duration-300",
        skin.shell,
        pop
          ? "shadow-[0_26px_64px_-18px_rgba(29,78,216,0.45)] ring-2 ring-primary/25 ring-offset-2 ring-offset-canvas lg:-mt-3 lg:mb-3"
          : "shadow-[0_18px_44px_-20px_rgba(15,23,42,0.28)] hover:shadow-[0_26px_58px_-22px_rgba(15,23,42,0.34)]",
      )}
      data-testid={`pricing-${pl.planId}`}
      data-plan-skin={pl.planId}
      data-commercial-state={pl.commercialVerified ? "verified" : "unavailable"}>
      {/* "Most popular" — attached to the top edge in the brand's secondary accent, so the anchor
          is marked by a BADGE and a lift rather than by shouting louder than its neighbours. */}
      {pop && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
          <span className="inline-flex items-center gap-1 rounded-full bg-[linear-gradient(135deg,#F97316,#FB923C)] text-royal-deep text-[11px] font-bold tracking-wide px-3.5 py-1.5 shadow-[0_8px_22px_rgba(249,115,22,0.35)] whitespace-nowrap">
            <Star className="w-3 h-3 fill-current" aria-hidden="true" /> Most popular
          </span>
        </div>
      )}

      {/* Identity row: the glyph sits BESIDE the name rather than in a stacked chip, so the card
          opens on what it is instead of on decoration, and the price gets the vertical room. */}
      <div className="flex items-center gap-3">
        <span className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl", skin.chip)}>
          <Icon className="h-[19px] w-[19px]" strokeWidth={1.9} aria-hidden="true" />
        </span>
        <h3 className={cn("font-heading font-bold text-[21px] leading-tight tracking-tight", skin.ink)}>
          {pl.name}
        </h3>
      </div>
      {pl.audience && (
        <p className={cn("mt-3 text-[13.5px] leading-snug min-h-[3.4rem]", skin.sub)}>{pl.audience}</p>
      )}

      {/* PRICE — the dominant element on the card, per the 2026 pricing-page research. Deliberately
          NOT a live region: five cards each announcing on every keystroke would fire five
          simultaneous interruptions; the page owns one polite summary instead. */}
      <div className={cn("mt-5 flex min-h-[16.25rem] flex-col border-t pt-5", skin.rule)}>
        {!pl.commercialVerified ? (
          <p className={cn("text-[15px]", skin.sub)} data-testid={`plan-price-unavailable-${pl.planId}`}>
            We couldn&apos;t load prices just now.
          </p>
        ) : pl.quoteOnly ? (
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className={cn("text-[14px]", skin.faint)}>{pl.priceLabel}</span>
            <span className={cn("font-heading font-extrabold text-[32px] tracking-tight leading-none tabular-nums", skin.ink)}
                  data-testid={`plan-price-${pl.planId}`}>
              {inrFull(pl.indicativePaise || pl.minimumPaise)}
            </span>
            <span className={cn("text-[14px] font-medium", skin.sub)}>a year</span>
          </div>
        ) : pl.termUnavailable ? (
          <div data-testid={`plan-term-unavailable-${pl.planId}`}>
            <div className="flex items-baseline gap-1 flex-wrap">
              <span className={cn("font-heading font-extrabold text-[32px] tracking-tight leading-none tabular-nums", skin.ink)}>
                {inrFull(pl.ratePaise)}
              </span>
              <span className={cn("text-[14px] font-medium", skin.sub)}>per student / year</span>
            </div>
            <p className={cn("mt-2 text-[12.5px] leading-snug", skin.sub)}>{pl.termUnavailableNote}</p>
            {pl.perStudentMonthPaise !== null && pl.perStudentMonthPaise !== undefined ? (
              <PriceTag key={pl.perStudentMonthPaise} skin={skin} planId={pl.planId}
                        monthPaise={pl.perStudentMonthPaise} yearPaise={pl.perStudentYearPaise}
                        note="" />
            ) : null}
          </div>
        ) : pl.ratePaise === 0 ? (
          <>
            <div className="flex items-baseline gap-1 flex-wrap">
              <span className={cn("font-heading font-extrabold text-[36px] tracking-tight leading-none whitespace-nowrap tabular-nums", skin.ink)}
                    data-testid={`plan-price-${pl.planId}`}>
                ₹0
              </span>
              <span className={cn("text-[14px] font-medium", skin.sub)}>free forever</span>
            </div>
            <PriceTag skin={skin} planId={pl.planId} monthPaise={0} yearPaise={0}
                      note=" · not a trial" />
          </>
        ) : pl.pricePaise === null ? (
          <>
            <div className="flex items-baseline gap-1 flex-wrap">
              <span className={cn("font-heading font-extrabold text-[32px] tracking-tight leading-none tabular-nums", skin.ink)}
                    data-testid={`plan-rate-${pl.planId}`}>
                {pl.ratePaise ? inrFull(pl.ratePaise) : "₹0"}
              </span>
              <span className={cn("text-[14px] font-medium", skin.sub)}>
                {pl.ratePaise ? "per student, a year" : "free forever"}
              </span>
            </div>
            {pl.perStudentMonthPaise !== null && pl.perStudentMonthPaise !== undefined ? (
              <PriceTag key={pl.perStudentMonthPaise} skin={skin} planId={pl.planId}
                        monthPaise={pl.perStudentMonthPaise} yearPaise={pl.perStudentYearPaise}
                        note="" />
            ) : null}
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-1 flex-wrap">
              <span className={cn("font-heading font-extrabold text-[36px] tracking-tight leading-none whitespace-nowrap tabular-nums", skin.ink)}
                    data-testid={`plan-price-${pl.planId}`}>
                {pl.pricePaise === 0 ? "₹0" : inrFull(pl.pricePaise)}
              </span>
              <span className={cn("text-[14px] font-medium", skin.sub)}>{termSuffix(pl)}</span>
            </div>
            {/* THE SAVING, in its own colour. A discount a reader has to compute is a discount
                they do not feel. Compared against paying MONTHLY for the same span, because that
                is the alternative a school actually has. */}
            {pl.savingVsMonthlyPaise ? (
              <p className={cn(
                "mt-3 inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold leading-none tabular-nums",
                skin.saving,
              )} data-testid={`plan-saving-${pl.planId}`}>
                <TrendingDown className="h-3 w-3 shrink-0" aria-hidden="true" />
                You save {inrFull(pl.savingVsMonthlyPaise)} {pl.savingBasis}
              </p>
            ) : null}
            {/* THE PER-STUDENT PRICE TAG — the number that actually persuades.
                Same price as the big figure above it, stated the way a head can hold it: a school
                reading "₹96,850 a year" is reading a budget line, and the same school reading
                "₹12 per student a month" is reading a decision they can take.

                So it is the one element that INVERTS its card — a solid light tag on the four deep
                cards, a solid navy tag on the orange one. It is the only surface here wearing the
                opposite value to everything around it, which is what makes the eye land on it, and
                it reads as a tag stuck onto the card rather than another translucent panel in a
                stack of translucent panels.

                `key` on the figure is load-bearing, not incidental: it re-mounts the node whenever
                the price changes, which replays the CSS entrance. The motion is therefore
                RESPONSIVE — it fires at the moment the number becomes true for THIS school, as the
                visitor types their roll — and never ambient. That is also the only motion this
                card is allowed: finiteMarketingMotion.test.js bans a looping treatment in this
                file, and is right to. A price that pulses forever is manufactured urgency, a named
                dark pattern under India's 2023 CCPA guidelines; a price that reacts once, when it
                changes, is feedback. See `.vp-price-tag` in index.css for why nothing here
                animates opacity. */}
            {pl.perStudentMonthPaise !== null && pl.perStudentMonthPaise !== undefined ? (
              <PriceTag key={pl.perStudentMonthPaise} skin={skin} planId={pl.planId}
                        monthPaise={pl.perStudentMonthPaise} yearPaise={pl.perStudentYearPaise}
                        note={pl.floorApplied ? " · at the plan minimum" : ""} />
            ) : null}
          </>
        )}
        {pl.floorNote ? (
          <p className={cn("mt-2 text-[12.5px] leading-snug", skin.sub)} data-testid={`plan-floor-${pl.planId}`}>
            {pl.floorNote}
          </p>
        ) : null}
        {/* The tax note follows its price naturally. It was `mt-auto`, which pinned it to the
            bottom of the reserved block and opened a visible hole between the price and its own
            footnote on the three cards with less to say — slack belongs AFTER the content, not
            inside it. The reserved height still does its job: the chip, the bullets and the CTA
            below all stay on one line across the five cards. */}
        <p className={cn("mt-1.5 text-[12px]", skin.faint)}>{pl.taxNote}</p>
        {campaignTrialLine ? (
          <p className={cn("mt-2 text-[12.5px] font-semibold leading-snug", campaignLineTheme.text)}
             data-testid={`plan-campaign-${pl.planId}`}>
            {campaignTrialLine}
          </p>
        ) : campaignDiscount > 0 ? (
          <p className={cn("mt-2 text-[12.5px] font-semibold leading-snug", campaignLineTheme.text)}
             data-testid={`plan-campaign-${pl.planId}`}>
            <span className="line-through opacity-70 mr-1">{inrCampaignExact(pl.pricePaise)}</span>
            {inrCampaignExact(pl.pricePaise - campaignDiscount)} for your first month with code{" "}
            {campaign.coupon.code}.
          </p>
        ) : null}
      </div>

      {/* HOW MUCH OF THE PRODUCT THIS PLAN IS. Five bullets can only ever be highlights, so the
          card states the SIZE of what it switches on and lets the bullets be the interesting part.
          Derived from the live entitlement registries (lib/marketingCounts) — never typed, because
          the number this replaces WAS typed: the page said "23 modules" while CORE_MODULES held
          28, understating what every school gets by five whole modules the day they were declared.
          It is also the bridge between this page and /modules: 58 modules is what a PLAN turns on,
          115 capabilities is what a school DOES with them. */}
      {pl.modulesIncluded && pl.modulesTotal ? (
        <div className={cn("mt-5 border-t pt-4", skin.rule)}>
          {/* A real disclosure now, not a static chip — click it to see every module this plan
              actually grants, grouped exactly like the comparison table below (same resolver,
              same platform-edit reactivity). All five cards expand together (state lives on the
              page, not the card), so a school can scan the same group down every column at once
              rather than one card growing awkwardly taller than its neighbours. */}
          <PlanModulesToggle
            planId={pl.planId}
            included={pl.modulesIncluded}
            total={pl.modulesTotal}
            expanded={modulesExpanded}
            onToggle={onToggleModules}
            skin={skin}
          />
          {modulesExpanded && modules ? (
            <PlanModulesList
              planId={pl.planId}
              coreGroups={modules.coreGroups}
              featureGroups={modules.featureGroups}
              skin={skin}
            />
          ) : null}
        </div>
      ) : null}

      {/* What the plan actually gives you. This list is the reason a card exists — it used to be
          empty on every card because the stored CMS was keyed on retired plan ids, which is the
          defect that made the whole grid unreadable. */}
      <ul className={cn("space-y-2.5 text-[13.5px] flex-1",
                        pl.modulesIncluded && pl.modulesTotal ? "mt-4" : "mt-5 border-t pt-5",
                        skin.rule, skin.sub)}
          data-testid={`plan-points-${pl.planId}`}>
        {(pl.points || []).map((x) => (
          <li key={x} className="flex items-start gap-2.5">
            <span className={cn("mt-[3px] grid h-4 w-4 shrink-0 place-items-center rounded-full", skin.tick)}>
              <Check className="h-2.5 w-2.5" strokeWidth={3.5} aria-hidden="true" />
            </span>
            <span className="leading-snug">{x}</span>
          </li>
        ))}
      </ul>

      {isEnterprise ? (
        <button type="button" onClick={() => {
          if (onContactSales) onContactSales();
          else navigate("/contact");
        }} className={cn(PRICE_BTN_BASE, skin.btn, "mt-6")}>
          {pl.cta || "Talk to us"} <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" aria-hidden="true" />
        </button>
      ) : (
        <Link to="/register" className={cn(PRICE_BTN_BASE, skin.btn, "mt-6")}>
          {pl.cta || CTA_PRIMARY_LABEL} <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" aria-hidden="true" />
        </Link>
      )}
    </StaggerItem>
  );
}

export function LandingPlanColumn({ pl, order = 1 }) {
  const Icon = PLAN_ICONS[pl.name] || PRICING_ICONS[pl.icon] || Zap;
  const featured = Boolean(pl.popular);
  return (
    <article
      className={cn(
        "relative flex min-w-0 flex-col px-5 py-7 sm:px-7 sm:py-8 lg:min-h-[34rem]",
        "border-t border-line first:border-t-0 lg:border-l lg:border-t-0 lg:first:border-l-0",
        featured && "bg-primary-50/70 dark:bg-primary/10",
      )}
      data-testid={`pricing-${pl.name}`}
      data-commercial-state={pl.commercialState || (pl.commercialVerified ? "verified" : "unavailable")}
    >
      {featured && <span className="absolute inset-x-0 top-0 h-1 bg-primary" aria-hidden="true" />}

      <div className="flex items-center justify-between gap-4">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/15 bg-surface text-primary shadow-card">
          <Icon className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />
        </span>
        <span className="font-heading text-[11px] font-bold uppercase tracking-[0.18em] text-ink-subtle">
          {String(order).padStart(2, "0")}
        </span>
      </div>

      <div className="mt-7 flex min-h-[6.75rem] flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-heading text-[25px] font-bold tracking-[-0.03em] text-ink">{pl.name}</h3>
          {featured && (
            <span className="rounded-full border border-primary/20 bg-surface px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.13em] text-accent-active">
              Most popular
            </span>
          )}
        </div>
        <p className="mt-2 max-w-[18rem] text-[14px] leading-6 text-ink-2">
          {pl.audience}
        </p>
      </div>

      <div className="mt-5 border-y border-line py-5">
        <div className="flex flex-wrap items-end gap-x-1.5 gap-y-1">
          <span className="font-heading text-[36px] font-extrabold leading-none tracking-[-0.04em] text-ink whitespace-nowrap">
            {pl.price}
          </span>
          {pl.period && <span className="pb-0.5 text-[13px] font-semibold text-ink-subtle">{pl.period}</span>}
        </div>
        {pl.billed && <p className="mt-2 text-[12.5px] leading-5 text-ink-subtle">{pl.billed}</p>}
        {pl.saving && (
          // `text-success-text`, not `-dark`: the same theme-aware-ink fix as `plan_free.accent`
          // above — this badge sits on the page's own theme-aware surface (not a fixed-colour card
          // `shell`), so the static light-only `-dark` ink would carry the identical dark-theme
          // contrast failure the moment this currently-unused column is wired up.
          <p className="mt-2 inline-flex w-fit self-start items-center rounded-full bg-success/12 px-2.5 py-1.5 text-[12px] font-semibold leading-none text-success-text ring-1 ring-success/20">
            {pl.saving}
          </p>
        )}
      </div>

      <ul className="mt-6 flex-1 space-y-4 text-[14px] text-ink-2">
        {(pl.features || []).map((feature) => (
          <li key={feature} className="flex items-start gap-3">
            <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
              <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
            </span>
            <span className="leading-6">{feature}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/register"
        className={cn(
          "mt-7 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full px-5 text-[14px] font-semibold",
          "transition-[background-color,border-color,color,transform] duration-fast ease-premium active:scale-[0.98]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2",
          featured
            ? "bg-primary text-primary-fg hover:bg-primary-700"
            : "border border-line-strong bg-surface text-ink hover:border-primary/45 hover:text-accent-active",
        )}
      >
        {pl.cta || CTA_PRIMARY_LABEL}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

export function LandingEnterpriseLane({ pl, navigate, onContactSales }) {
  const Icon = PLAN_ICONS[pl.name] || PRICING_ICONS[pl.icon] || Zap;
  return (
    <article
      className="relative overflow-hidden border-t border-white/15 bg-vidya-950 px-5 py-7 text-white sm:px-7 sm:py-8 lg:grid lg:grid-cols-[0.8fr_1.2fr_auto] lg:items-center lg:gap-9"
      data-testid={`pricing-${pl.name}`}
      data-commercial-state={pl.commercialState || "proposal"}
    >
      <div className="pointer-events-none absolute inset-y-0 right-0 w-2/5 bg-[radial-gradient(circle_at_80%_50%,rgba(37,99,235,0.23),transparent_66%)]" aria-hidden="true" />
      <div className="relative flex items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/15 bg-white/10 text-white">
          <Icon className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />
        </span>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/65">Multi-school procurement</p>
          <h3 className="mt-1 font-heading text-[26px] font-bold tracking-[-0.03em] text-white">{pl.name}</h3>
          <p className="mt-2 max-w-sm text-[13.5px] leading-6 text-white/70">
            {pl.audience}
          </p>
        </div>
      </div>

      <ul className="relative mt-6 grid gap-3 text-[13.5px] text-white/75 sm:grid-cols-2 lg:mt-0">
        {(pl.features || []).map((feature) => (
          <li key={feature} className="flex items-start gap-2.5">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#BAE6FD]" strokeWidth={2.5} aria-hidden="true" />
            <span className="leading-5">{feature}</span>
          </li>
        ))}
      </ul>

      <div className="relative mt-7 flex flex-col items-start gap-3 lg:mt-0 lg:items-end">
        <span className="font-heading text-[28px] font-bold tracking-[-0.03em] text-white">{pl.price}</span>
        <button
          type="button"
          onClick={() => {
            if (onContactSales) onContactSales();
            else navigate("/contact");
          }}
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full !bg-white px-5 text-[14px] font-bold !text-vidya-950 transition-transform duration-fast ease-premium active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-vidya-950 lg:w-auto"
        >
          {pl.cta || "Contact sales"}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}
